// client/src/utils/clientThemeFallback.js
// Compute a per-image fallback theme when backend returns theme=null.
//
// Requirements:
// - Must return primaryBackground + WCAG-safe primary/secondary text colors (4.5:1).
// - Must be deterministic per image and return a fresh object (no shared references).

import { pickReadableTextColors } from './contrast';

const clamp01 = (n) => Math.max(0, Math.min(1, n));

const rgbToHex = (r, g, b) =>
  `#${[r, g, b].map((v) => v.toString(16).padStart(2, '0')).join('')}`.toLowerCase();

const quantize = (v, step) => Math.max(0, Math.min(255, Math.round(v / step) * step));

export function computeThemeFromPixels({ data, width, height }) {
  // data = Uint8ClampedArray RGBA
  if (!data || !width || !height) {
    const fallback = { primaryBackground: '#f5f5f5', ...pickReadableTextColors('#f5f5f5') };
    return { ...fallback };
  }

  const hist = new Map();
  const stepPx = 4; // sample grid stride
  const q = 12;

  for (let y = 0; y < height; y += stepPx) {
    for (let x = 0; x < width; x += stepPx) {
      const i = (y * width + x) * 4;
      const r = data[i];
      const g = data[i + 1];
      const b = data[i + 2];
      const a = data[i + 3];

      if (a < 200) continue;
      // Skip extreme blacks (noise)
      if (r < 10 && g < 10 && b < 10) continue;
      // Skip near-white glare
      if (r > 250 && g > 250 && b > 250) continue;

      const qr = quantize(r, q);
      const qg = quantize(g, q);
      const qb = quantize(b, q);
      const key = `${qr},${qg},${qb}`;
      hist.set(key, (hist.get(key) || 0) + 1);
    }
  }

  let bestKey = null;
  let bestScore = -1;
  for (const [key, count] of hist.entries()) {
    // mild midtone bias to avoid glare/shadows dominating
    const [r, g, b] = key.split(',').map((n) => parseInt(n, 10));
    const lum = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255; // approx 0..1
    const midBias = 1 - Math.abs(lum - 0.5) * 0.35;
    const score = count * clamp01(midBias);
    if (score > bestScore) {
      bestScore = score;
      bestKey = key;
    }
  }

  const bg = bestKey
    ? (() => {
        const [r, g, b] = bestKey.split(',').map((n) => parseInt(n, 10));
        return rgbToHex(r, g, b);
      })()
    : '#f5f5f5';

  const text = pickReadableTextColors(bg, { minContrast: 4.5 });
  return {
    primaryBackground: bg,
    primaryText: text.primaryText,
    secondaryText: text.secondaryText
  };
}

export async function computeClientFallbackThemeFromBlob(blob) {
  const imgUrl = URL.createObjectURL(blob);
  try {
    // Prefer createImageBitmap where available (fast decode)
    const bitmap =
      typeof createImageBitmap === 'function'
        ? await createImageBitmap(blob)
        : await new Promise((resolve, reject) => {
            const img = new Image();
            img.decoding = 'async';
            img.onload = () => resolve(img);
            img.onerror = reject;
            img.src = imgUrl;
          });

    const maxEdge = 220; // small sampling canvas
    const scale = Math.min(1, maxEdge / Math.max(bitmap.width, bitmap.height));
    const w = Math.max(1, Math.round(bitmap.width * scale));
    const h = Math.max(1, Math.round(bitmap.height * scale));

    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    // light blur helps reduce text noise for palette sampling
    ctx.filter = 'blur(1px)';
    ctx.drawImage(bitmap, 0, 0, w, h);
    const imageData = ctx.getImageData(0, 0, w, h);
    const theme = computeThemeFromPixels({ data: imageData.data, width: w, height: h });
    // Always return a fresh object
    return { ...theme };
  } finally {
    try {
      URL.revokeObjectURL(imgUrl);
    } catch {
      // ignore
    }
  }
}

